// Pure rules of News Management: states, the form ↔ API payload, validation and audience labels.
// No imports from the app, so `npm test` runs them as they are.

// Mirrors MAX_BODY_BYTES in webback (`app/blueprints/announcements/constants.py`), so a 5 MB file
// fails here instead of after the upload
export const MAX_BODY_BYTES = 256 * 1024

export const STATE_LABELS = { live: 'Live', scheduled: 'Scheduled', expired: 'Expired', retracted: 'Retracted' }

// The values the `state` filter sends, next to the labels of the status pill, so the filter
// cannot drift into meaning something different from the column it filters
export const STATE_FILTER_OPTIONS = [
	{ value: 'all', label: 'All' },
	...Object.entries(STATE_LABELS).map(([value, label]) => ({ value, label })),
]

export const DISPLAY_MODES = [
	{ value: 'once', label: 'Once per user', hint: 'One popup per user, ever' },
	// Not "every visit": the feed is read once per app load, not on each navigation
	{ value: 'always', label: 'Always', hint: 'Comes back on every app reload while the window is open' },
]

// Not the `active` flag alone: whether it reaches anyone right now. Published but not open yet,
// and published but closed, both have `active` while reaching nobody.
export function announcementState(row, now = new Date()) {
	if (!row.active) return 'retracted'
	if (row.start_at && now < new Date(row.start_at)) return 'scheduled'
	if (row.end_at && now > new Date(row.end_at)) return 'expired'
	return 'live'
}

const startOfDay = date => {
	const day = new Date(date)
	day.setHours(0, 0, 0, 0)
	return day
}

// The date picker hands back midnight: left as is, "ends on the 30th" would stop the
// announcement as the 30th began, a day short of what was asked
const endOfDay = date => {
	if (!date) return null
	const day = new Date(date)
	day.setHours(23, 59, 59, 0)
	return day
}

// Retail's defaults: starts today, no end, once per user, published, everyone
export const emptyForm = (now = new Date()) => ({
	title: '',
	body: '',
	fileName: '',
	display_mode: 'once',
	audience_companies: [],
	audience_users: [],
	start_at: startOfDay(now),
	end_at: null,
	active: true,
})

export const fromRow = row => ({
	title: row.title ?? '',
	body: row.body ?? '',
	fileName: '',
	display_mode: row.display_mode ?? 'once',
	// null means everyone in the API; the pickers show that as no selection
	audience_companies: row.audience_companies ?? [],
	audience_users: row.audience_users ?? [],
	start_at: row.start_at ? new Date(row.start_at) : startOfDay(new Date()),
	end_at: row.end_at ? new Date(row.end_at) : null,
	active: !!row.active,
})

// UTC with its Z: the API compares against MySQL's NOW(), which is UTC. A local midnight start
// is sent as the UTC instant it is, so "starts on the 1st" still means the publisher's 1st.
export const toPayload = form => ({
	title: form.title.trim(),
	body: form.body,
	display_mode: form.display_mode,
	// An empty picker means everyone, which the API spells null; it refuses []
	audience_companies: form.audience_companies.length ? form.audience_companies : null,
	audience_users: form.audience_users.length ? form.audience_users : null,
	start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
	end_at: form.end_at ? endOfDay(form.end_at).toISOString() : null,
	active: form.active,
})

export const bodyBytes = body => new TextEncoder().encode(body || '').length

export function validate(form) {
	const errors = {}
	if (!form.title.trim()) errors.title = 'Required'
	if (form.title.length > 200) errors.title = 'Up to 200 characters'
	if (!form.body.trim()) errors.body = 'Upload an .html file or paste the HTML'
	if (bodyBytes(form.body) > MAX_BODY_BYTES) errors.body = 'Over 256 KB. Link images by URL instead of embedding them.'
	if (!form.start_at) errors.start_at = 'Required'
	if (form.end_at && form.start_at && endOfDay(form.end_at) <= new Date(form.start_at))
		errors.end_at = 'Must be later than the start date'
	return errors
}

// Published, aimed at everyone and already open: a Save reaches every user right away
export const reachesEveryoneNow = (form, now = new Date()) =>
	!!form.active && !form.audience_companies.length && !form.audience_users.length
	&& !!form.start_at && new Date(form.start_at) <= now

// Who an announcement is for, by name. A company or user saved before being disabled keeps its
// name and says so; a uid no longer in USER reads "Unknown user", never a raw id.
export function audienceEntries(row, audiences) {
	const companies = new Map((audiences?.companies ?? []).map(c => [c.company, c]))
	const users = new Map((audiences?.users ?? []).map(u => [u.uid, u]))
	return {
		companies: (row.audience_companies ?? []).map(name => ({ name, enabled: companies.get(name)?.enabled ?? false })),
		users: (row.audience_users ?? []).map(uid => {
			const user = users.get(uid)
			return { name: user?.username ?? 'Unknown user', enabled: user?.enabled ?? false }
		}),
	}
}

const usersLabel = n => `${n} user${n === 1 ? '' : 's'}`

export function audienceSummary(row) {
	const companies = row.audience_companies?.length ?? 0
	const users = row.audience_users?.length ?? 0
	if (!companies && !users) return 'Everyone'
	return [
		companies ? `${companies} ${companies === 1 ? 'company' : 'companies'}` : null,
		users ? usersLabel(users) : null,
	].filter(Boolean).join(' · ')
}

export const seenBy = count => (!count ? '—' : usersLabel(count))
